/**
 * /dashboard/posh/complaints/$id/manage — ICC/admin complaint detail +
 * actions.
 *
 * GET /posh/complaints/{id} — STRICT 2FA. A non-TOTP-enrolled caller is
 * 403'd by the backend; we detect that exact 403 and render
 * <TwoFactorRequired/> with a /2fa-setup link.
 *
 * 2026-10 (QA P0, POSH Act 2013 §16): the page renders what the server's
 * access matrix sent (detail "access.view"): the Internal Committee's case
 * file with its actions; the HR/admin REGISTER view (metadata + the
 * employer's §13(3) action); or — the caller filed this complaint — their
 * own party view (withdraw / add evidence only). A respondent never gets
 * here: the server answers 404.
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/posh/complaints/[id]/manage/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - next/link Link → @tanstack/react-router Link
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { toApiError } from "@/lib/api";
import { is2FARequiredError, useComplaint } from "@/hooks/use-posh";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { TwoFactorRequired } from "@/components/posh/two-factor-required";
import { ComplaintDetailView } from "@/components/posh/complaint-detail-view";
import { ComplaintManageActions } from "@/components/posh/complaint-manage-actions";
import { ComplaintInquirySections } from "@/components/posh/complaint-inquiry-sections";
import { ComplaintRegisterView } from "@/components/posh/complaint-register-view";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/posh/complaints/$id/manage",
)({
  component: ManageComplaintRoute,
});

function ManageComplaintRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const q = useComplaint(Number(id));

  return (
    <PoshModuleShell
      title="Complaint Administration"
      description="Committee view: status, ICC assignment and documents. This area requires two-factor authentication."
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/posh/complaints" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </Button>
      }
    >
      <PoshFeatureGate>
        {q.isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}

        {q.isError && is2FARequiredError(q.error) && (
          <TwoFactorRequired surface="ICC complaint administration" />
        )}

        {q.isError && !is2FARequiredError(q.error) && (
          <Alert variant="destructive">
            <AlertTitle>Could not load this complaint</AlertTitle>
            <AlertDescription>
              {toApiError(q.error).message}
            </AlertDescription>
          </Alert>
        )}

        {q.data && q.data.access?.view === "register" && (
          <ComplaintRegisterView data={q.data} />
        )}

        {q.data && q.data.access?.view === "complainant" && (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2">
              <Alert>
                <AlertTitle>You filed this complaint</AlertTitle>
                <AlertDescription>
                  The Internal Committee handles it — you cannot act on your
                  own complaint (POSH Act 2013 §16). You can add evidence or
                  withdraw it.
                </AlertDescription>
              </Alert>
              <ComplaintDetailView data={q.data} canDownload />
            </div>
            <div>
              <ComplaintManageActions
                c={q.data.complaint}
                access={q.data.access}
              />
            </div>
          </div>
        )}

        {q.data &&
          (q.data.access === undefined ||
            q.data.access.view === "case_file") && (
            <div className="grid gap-6 lg:grid-cols-3">
              <div className="space-y-6 lg:col-span-2">
                <ComplaintDetailView
                  data={q.data}
                  canDownload
                  showInquiryRecords={false}
                  showIntake
                />
                {/* §12/§13/§13(4)/§18 editable sections (migration 090).
                    Inherits the manage route's strict-2FA ICC gate. */}
                <ComplaintInquirySections data={q.data} />
              </div>
              <div>
                <ComplaintManageActions
                  c={q.data.complaint}
                  access={q.data.access}
                />
              </div>
            </div>
          )}
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
